import { useState, useEffect } from 'react';
import { XMarkIcon, MagnifyingGlassIcon, UserIcon } from '@heroicons/react/24/outline';

interface Player {
  id: string;
  first_name: string;
  last_name: string;
  category: string;
  country: string;
  resident_club?: string;
}

interface Props {
  isOpen: boolean;
  onClose: () => void;
  players: Player[];
  playersWithScores: Record<string, number[]>;
  onSelectPlayer: (playerId: string) => void;
}

export default function MobilePlayerSelectionSheet({
  isOpen,
  onClose,
  players,
  playersWithScores,
  onSelectPlayer,
}: Props) {
  const [search, setSearch] = useState('');
  const [filtered, setFiltered] = useState<Player[]>([]);

  useEffect(() => {
    if (isOpen) {
      setSearch('');
      setFiltered(players);
    }
  }, [isOpen, players]);

  useEffect(() => {
    const term = search.toLowerCase();
    if (!term.trim()) {
      setFiltered(players);
      return;
    }
    setFiltered(
      players.filter(p =>
        `${p.first_name} ${p.last_name}`.toLowerCase().includes(term) ||
        p.country.toLowerCase().includes(term) ||
        (p.resident_club || '').toLowerCase().includes(term)
      )
    );
  }, [search, players]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex flex-col justify-end">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white rounded-t-3xl shadow-2xl max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between px-5 pt-5 pb-3 border-b border-gray-100 flex-shrink-0">
          <div>
            <h2 className="text-base font-bold text-golf-gray">Select Player</h2>
            <p className="text-xs text-gray-400 mt-0.5">{players.length} player{players.length !== 1 ? 's' : ''} available</p>
          </div>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-xl bg-gray-100 active:bg-gray-200 transition-colors">
            <XMarkIcon className="w-4 h-4 text-gray-500" />
          </button>
        </div>

        <div className="px-5 py-3 border-b border-gray-100 flex-shrink-0">
          <div className="relative">
            <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search by name, country, or club..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              autoFocus
              className="w-full pl-9 pr-4 py-2.5 text-sm border border-gray-200 rounded-xl focus:outline-none focus:border-golf-orange bg-gray-50"
            />
          </div>
        </div>

        <div className="flex-1 overflow-y-auto">
          {filtered.length === 0 ? (
            <div className="py-16 text-center">
              <UserIcon className="w-10 h-10 text-gray-200 mx-auto mb-3" />
              <p className="text-sm text-gray-400">
                {search ? `No players found for "${search}"` : 'No players available'}
              </p>
            </div>
          ) : (
            <div className="divide-y divide-gray-100">
              {filtered.map(player => (
                <div key={player.id} className="px-5 py-3.5 flex items-center gap-3">
                  <div className="w-9 h-9 rounded-full bg-golf-orange/10 flex items-center justify-center flex-shrink-0">
                    <UserIcon className="w-5 h-5 text-golf-orange" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-golf-gray truncate">
                      {player.first_name} {player.last_name}
                    </p>
                    <p className="text-xs text-gray-500 truncate">
                      {player.category} · {player.country}
                    </p>
                    {playersWithScores[player.id]?.length > 0 && (
                      <div className="flex flex-wrap gap-1 mt-1">
                        {playersWithScores[player.id].map(r => (
                          <span key={r} className="text-xs bg-gray-100 text-gray-600 rounded-full px-1.5 py-0.5">R{r}</span>
                        ))}
                      </div>
                    )}
                  </div>
                  <button
                    onClick={() => {
                      onSelectPlayer(player.id);
                      onClose();
                    }}
                    className="flex-shrink-0 px-3 py-1.5 bg-golf-orange text-white text-xs font-semibold rounded-xl active:bg-golf-orange/90 transition-colors"
                  >
                    Add Score
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="px-5 py-4 border-t border-gray-100 flex-shrink-0">
          <button
            onClick={onClose}
            className="w-full py-3 rounded-xl border border-gray-200 text-sm font-semibold text-gray-600 bg-white active:bg-gray-50 transition-colors"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}
